import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { captureException } from '@sentry/browser'
import classnames from 'classnames'
import { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Icon } from 'semantic-ui-react'

import { Tooltip } from 'ui'

import type { FC } from 'react'
import type { SemanticICONS } from 'semantic-ui-react'

interface Section {
  key: string
  access: number
  icon: FC<{ className?: string }> | SemanticICONS
  name: string

  description?: string
  desktop?: boolean
  highlight?: boolean
  mobile?: boolean
  status?: string[]
}

interface SectionsMenuProps {
  baseURL: string
  sections: Section[]

  activeKey?: string
  className?: string
  currentAccess?: number
  filterOption?: (section: Section) => boolean
}

const SectionsMenu: FC<SectionsMenuProps> = ({
  activeKey,
  baseURL,
  className,
  currentAccess = 0,
  filterOption,
  sections,
}) => {
  // @ts-ignore
  const isMobile = useSelector((state) => state.environment.stage.small)
  const filteredSections = sections.filter((s) => {
    if ((isMobile && !s.mobile) || (!isMobile && !s.desktop)) {
      return false
    }

    if (filterOption && !filterOption(s)) {
      return false
    }

    return !s.access || currentAccess & s.access
  })

  let activeSection: Section = {
    key: '',
    access: 111,
    icon: 'user',
    name: '',
  }
  try {
    activeSection = filteredSections.find((item) => item.key === activeKey) || filteredSections.at(0) || activeSection
  } catch (e) {
    captureException(e, { extra: { activeKey, filteredSections } })
    // eslint-disable-next-line no-console
    console.error(e)
  }

  if (!activeSection.key) {
    return null
  }

  return (
    <Popover className={classnames('relative mx-auto max-w-screen-1366', className)}>
      <div className="flex items-center gap-2 py-1 px-2 border-b bg-white">
        <span className="font-bold">Sección: </span>
        <PopoverButton className="inline-flex items-center p-1 gap-1 outline-0 cursor-pointer rounded-lg border border-greyLight bg-white focus:border-greenLight hover:border-greenLight text-lg text-green">
          {typeof activeSection.icon === 'string' ? (
            <Icon fitted name={activeSection.icon} />
          ) : (
            <activeSection.icon aria-hidden="true" className="size-5" />
          )}
          <div className="truncate">{activeSection.name}</div>
          <ChevronDownIcon aria-hidden="true" className="size-5" />
        </PopoverButton>
        {filteredSections
          .filter((s) => s.highlight && s.key !== activeSection.key)
          .slice(0, 2)
          .map((item) => (
            <Tooltip key={item.key} tooltip={item.name}>
              <Link className="!no-underline" to={`${baseURL}/${item.key}`}>
                <div className="flex items-center justify-center size-9 bg-white border border-greyLight rounded-lg group-hover:border-inherit hover:border-orangeLight  !text-orangeLight">
                  {typeof item.icon === 'string' ? (
                    <Icon className="!m-0" name={item.icon} />
                  ) : (
                    <item.icon aria-hidden="true" className="size-5" />
                  )}
                </div>
              </Link>
            </Tooltip>
          ))}
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel className="absolute z-10 flex px-2 pb-2 -mt-1 max-w-full">
          {({ close }) => (
            <div className="grid grid-cols-1 800:grid-cols-2 1024:grid-cols-3 gap-1 rounded-xl bg-white shadow-lg ring-1 ring-greyLightMed p-1 border border-greenLight overflow-hidden">
              {filteredSections.map((item) => (
                <Link
                  key={item.key}
                  className={classnames(
                    'group !no-underline max-w-80 flex items-center gap-2 rounded-md p-2 border hover:bg-greyLightest hover:border-greyLight hover:!text-green',
                    item.key !== activeSection.key && item.highlight && 'border-orangeLight !text-orangeLight',
                    item.key === activeSection.key
                      ? 'bg-greyLightest border-greyLight !text-green'
                      : 'border-greyLightest !text-text',
                  )}
                  onClick={() => close()}
                  to={`${baseURL}/${item.key}`}
                >
                  <div className="flex items-center justify-center size-10 bg-white border border-greyLight rounded-lg group-hover:border-inherit">
                    {typeof item.icon === 'string' ? (
                      <Icon className="!m-0" name={item.icon} />
                    ) : (
                      <item.icon aria-hidden="true" className="size-5" />
                    )}
                  </div>
                  <div>
                    <span className="font-bold">{item.name}</span>
                    {!!item.description && <p className="text-greyDark text-sm">{item.description}</p>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  )
}

export default SectionsMenu
